import dayjs from "dayjs";
import { Check } from "lucide-react";
import { TableRow, TableCell } from "@hotelise/ui";

import { cn } from "utils/cn";

import { Statement } from "interfaces/statements";

import StatementsClient from "services/api/statements";

interface Props {
	data: Statement;
	hideEditAction?: boolean;
	hideDeleteAction?: boolean;
	showProperty?: boolean;
	visibleColumnIds?: string[];
	gridTemplateColumns?: string;
	isMobile?: boolean;
	viewMode?: "compact" | "detailed";
}

const StatementListItem = (props: Props): JSX.Element => {
	const {
		data,
		showProperty,
		visibleColumnIds,
		isMobile,
		viewMode,
	} = props;

	const { month, year, reportData, createdAt, propertyId, id, reviewed } = data;

	const handleDownload = async () => {
		try {
			const res = await StatementsClient.getStatementDownloadLink({
				propertyId,
				statementId: id,
			});

			if (res.status === 200) {
				const { url } = res.data;
				const link = document.createElement("a");
				link.setAttribute("download", "report");
				link.setAttribute("target", "_blank");
				link.href = url;
				document.body.appendChild(link);
				link.click();
			}
		} catch (error) {
			console.log({ error });
		}
	};

	const periodText = `${dayjs()
		.month(+month - 1)
		.format("MMMM")}, ${year}`;

	const verifiedBadge = (
		<div
			className={cn(
				"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
				reviewed
					? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
					: "bg-muted text-muted-foreground"
			)}
		>
			{reviewed && <Check className="h-3 w-3" />}
			{reviewed ? "Verified" : "Draft"}
		</div>
	);

	// Legacy statements (empty months generated before ~03/2026) can lack
	// calculations.totalInCurrency entirely — render dashes instead of crashing.
	const totals = reportData?.calculations?.totalInCurrency;
	const incomeText = totals?.totalIncomeInCurrency ?? "—";
	const expensesText = totals?.totalExpensesInCurrency ?? "—";
	const netIncomeText = totals?.netOwnerIncomeInCurrency ?? "—";
	const reservationsCount = reportData?.reportItemsLength ?? 0;

	const amdTransfer = reportData?.calculations?.mergedAccountTransfers?.find(
		(t) => t._currency === "AMD"
	);
	const ownerTransferValue = amdTransfer?._toTransfer ?? 0;
	const hoteliseHelpValue = totals?.totalHoteliseFeeInCurrency ?? 0;

	if (isMobile) {
		const isDetailed = viewMode === "detailed";

		return (
			<div className="rounded-lg border bg-card p-3 space-y-2 shadow-sm">
				<div className="flex items-start justify-between gap-2">
					<div className="min-w-0">
						<div className="font-semibold text-sm">{periodText}</div>
						{showProperty && data?.propertyName && (
							<div className="text-xs text-muted-foreground truncate">{data.propertyName}</div>
						)}
					</div>
					<span className="text-[11px] text-muted-foreground shrink-0">
						#{data.statementNumber || "—"}
					</span>
				</div>

				<div className="flex items-center justify-between text-xs">
					<span className="text-muted-foreground">
						{reservationsCount} reservations
					</span>
					<span className="font-semibold text-green-700">
						{incomeText}
					</span>
				</div>

				<div className="flex items-center justify-between text-xs pt-1 border-t">
					<button
						onClick={handleDownload}
						className="border-none outline-none cursor-pointer bg-transparent text-primary font-medium text-xs transition-opacity hover:opacity-80 hover:underline p-0"
					>
						Download PDF
					</button>
					{verifiedBadge}
				</div>

				{isDetailed && (
					<div className="border-t border-border pt-2 text-xs tabular-nums space-y-1">
						<div className="grid grid-cols-2 gap-x-4 gap-y-1">
							<div className="flex justify-between items-center">
								<span className="text-muted-foreground">Expenses</span>
								<span className="font-medium">{expensesText}</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-muted-foreground">Paid Out</span>
								<span className="font-medium">{netIncomeText}</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-muted-foreground">Hotelise Help</span>
								<span className="font-medium">֏{hoteliseHelpValue}</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-muted-foreground">Created At</span>
								<span className="font-medium">{dayjs(createdAt).format("D MMM, YYYY")}</span>
							</div>
						</div>
						<div className="flex justify-end items-center border-t border-border pt-1">
							<div className="flex items-center gap-1.5">
								<span className="text-foreground font-semibold">Owner Transfer</span>
								<span className="font-semibold">֏{ownerTransferValue}</span>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	}

	const isVisible = (colId: string) => {
		if (!visibleColumnIds) return true;
		return visibleColumnIds.includes(colId);
	};

	return (
		<>
			<TableRow>
				{isVisible("statementNumber") && (
					<TableCell className="text-sm">
						<button
							onClick={handleDownload}
							className="border-none outline-none cursor-pointer bg-transparent text-primary font-medium transition-opacity hover:opacity-80 hover:underline p-0"
						>
							{data.statementNumber || "—"}
						</button>
					</TableCell>
				)}
				{showProperty && isVisible("property") && (
					<TableCell className="text-sm">{data?.propertyName || ""}</TableCell>
				)}
				{isVisible("period") && (
					<TableCell className="text-sm">{periodText}</TableCell>
				)}
				{isVisible("reservations") && (
					<TableCell className="text-sm">{reservationsCount}</TableCell>
				)}
				{isVisible("income") && (
					<TableCell className="text-sm">
						{incomeText}
					</TableCell>
				)}
				{isVisible("verified") && (
					<TableCell className="text-center">
						{verifiedBadge}
					</TableCell>
				)}
				{isVisible("viewInvoice") && (
					<TableCell>
						<button
							onClick={handleDownload}
							className="border-none outline-none cursor-pointer bg-transparent text-primary font-medium transition-opacity hover:opacity-80 hover:underline"
						>
							PDF
						</button>
					</TableCell>
				)}
			</TableRow>
			{viewMode === "detailed" && (
				<TableRow className="bg-muted/30 hover:bg-muted/40">
					<TableCell colSpan={visibleColumnIds?.length ?? (showProperty ? 7 : 6)} className="py-2 px-4">
						<div className="flex items-center gap-6 text-xs tabular-nums">
							<div className="flex items-center gap-1.5">
								<span className="text-muted-foreground">Expenses</span>
								<span className="font-medium">{expensesText}</span>
							</div>
							<div className="flex items-center gap-1.5">
								<span className="text-muted-foreground">Paid Out</span>
								<span className="font-medium">{netIncomeText}</span>
							</div>
							<div className="flex items-center gap-1.5">
								<span className="text-muted-foreground">Hotelise Help</span>
								<span className="font-medium">֏{hoteliseHelpValue}</span>
							</div>
							<div className="h-3 w-px bg-border" />
							<div className="flex items-center gap-1.5">
								<span className="text-foreground font-semibold">Owner Transfer</span>
								<span className="font-semibold">֏{ownerTransferValue}</span>
							</div>
							<div className="ml-auto flex items-center gap-1.5">
								<span className="text-muted-foreground">Created At</span>
								<span className="font-medium">{dayjs(createdAt).format("D MMM, YYYY")}</span>
							</div>
						</div>
					</TableCell>
				</TableRow>
			)}
		</>
	);
};

export default StatementListItem;